import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { BlogArticle } from "@/components/blog/BlogArticle";
import { SITE_URL } from "@/lib/i18n";
import { buildHead, breadcrumbJsonLd, ROBOTS_NOINDEX_NOFOLLOW } from "@/lib/seo";
import { publicBlogPostPageQuery } from "@/lib/query-registry";
import { setEdgeHtmlCache, markNotFoundResponse } from "@/lib/cache-headers";
import { blogCategoryLabel } from "@/lib/blog-categories";
import { rankingStructuredData } from "@/lib/blog-ranking-schema";
import type { BlogPostFull } from "@/components/blog/blog-types";

import { resolveLegacyBlogPath } from "@/lib/blog-legacy-redirects";

// The public query selects "*", so the Spanish columns are present at runtime
// even though BlogPostFull does not declare them.
function spanishSiblingPath(post: BlogPostFull): string | null {
  const row = post as BlogPostFull & {
    slug_es?: string | null;
    title_es?: string | null;
    content_es?: string | null;
  };
  return row.slug_es?.trim() && row.title_es?.trim() && row.content_es?.trim()
    ? `/es/blog/${row.slug_es}`
    : null;
}

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params, context }) => {
    await setEdgeHtmlCache();

    // Legacy taxonomy path guard (tag archives were never indexable).
    if (params.slug === "tag") {
      throw redirect({ to: "/blog", statusCode: 301 });
    }

    const legacy = resolveLegacyBlogPath(params.slug);
    if (legacy) {
      throw redirect({ href: legacy, statusCode: 301 });
    }

    const page = await context.queryClient.ensureQueryData(
      publicBlogPostPageQuery({ slug: params.slug, relatedLimit: 3 }),
    );
    if (!page.post) throw notFound();
    return page;
  },
  head: ({ loaderData, params }) => {
    const post = loaderData?.post as BlogPostFull | null | undefined;
    if (!post) {
      return {
        meta: [
          { title: "Insight not found · Shugert" },
          { name: "robots", content: ROBOTS_NOINDEX_NOFOLLOW },
        ],
        links: [],
      };
    }

    const path = `/blog/${params.slug}`;
    const publishedAt = post.published_at ?? post.created_at ?? undefined;
    const title = `${post.meta_title || post.title} · Shugert`;
    const description = post.meta_description || post.excerpt || post.title;
    const categoryLabel = blogCategoryLabel(post.category);
    const imageAbs =
      post.og_image || post.featured_image
        ? ((post.og_image || post.featured_image) as string).startsWith("http")
          ? ((post.og_image || post.featured_image) as string)
          : `${SITE_URL}${post.og_image || post.featured_image}`
        : undefined;

    const keywords = Array.from(
      new Set(
        [post.focus_keyword, ...(post.secondary_keywords ?? []), ...(post.tags ?? [])].filter(
          Boolean,
        ) as string[],
      ),
    );

    const articleLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": post.schema_type === "Article" ? "Article" : "BlogPosting",
      headline: post.meta_title || post.title,
      description,
      image: imageAbs ? [imageAbs] : undefined,
      datePublished: publishedAt,
      dateModified: post.updated_at ?? publishedAt,
      author: {
        "@type": "Person",
        name: post.author,
        ...(post.author === "Samuel Noriega"
          ? { url: `${SITE_URL}/about/samuel-noriega`, jobTitle: "Founder, Shugert Marketing" }
          : {}),
      },
      publisher: {
        "@type": "Organization",
        "@id": `${SITE_URL}#org`,
        name: "Shugert",
        logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
      },
      mainEntityOfPage: `${SITE_URL}${path}`,
      articleSection: categoryLabel,
      ...(keywords.length ? { keywords } : {}),
    };

    return buildHead({
      locale: "en",
      title,
      description,
      path,
      // Pair with the Spanish sibling when one is published so both pages
      // carry reciprocal hreflang annotations.
      altPath: spanishSiblingPath(post) ?? path,
      type: "article",
      canonical: post.canonical_url || undefined,
      noindex: post.noindex,
      ogImage: imageAbs,
      ogTitle: post.og_title || undefined,
      ogDescription: post.og_description || undefined,
      twitterTitle: post.twitter_title || undefined,
      twitterDescription: post.twitter_description || undefined,
      author: post.author,
      articleAuthor: post.author,
      articleSection: categoryLabel,
      publishedTime: publishedAt,
      modifiedTime: post.updated_at ?? undefined,
      jsonLd: [
        articleLd,
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Insights", path: "/blog" },
          { name: categoryLabel, path: `/blog/category/${post.category}` },
          { name: post.title, path },
        ]),
        ...rankingStructuredData(params.slug),
      ],
    });
  },
  notFoundComponent: () => {
    markNotFoundResponse();
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-6 py-32 text-center">
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-muted-foreground">
            404
          </p>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-foreground">
            Post not found
          </h1>
          <Link
            to="/blog"
            className="mt-8 inline-flex items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background"
          >
            Browse insights
          </Link>
        </div>
      </SiteShell>
    );
  },
  errorComponent: ({ error }) => (
    <SiteShell>
      <div className="mx-auto max-w-2xl px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-semibold text-foreground">
          Something went wrong
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">{error.message}</p>
        <Link
          to="/blog"
          className="mt-8 inline-flex items-center justify-center rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground"
        >
          Back to insights
        </Link>
      </div>
    </SiteShell>
  ),
  component: BlogPostRoute,
});

function BlogPostRoute() {
  const { post, relatedPosts, relatedResources, relatedCaseStudies } = Route.useLoaderData();
  return (
    <SiteShell>
      <BlogArticle
        post={post as BlogPostFull}
        relatedPosts={relatedPosts}
        relatedResources={relatedResources}
        relatedCaseStudies={relatedCaseStudies}
      />
    </SiteShell>
  );
}
